import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type {
  Response,
  Vendor,
  VendorRemoteProductsPayload,
} from "../interfaces";

export const vendorApi = createApi({
  reducerPath: "vendorApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Vendor", "VendorRemoteProducts"],
  endpoints: (builder) => ({
    getVendors: builder.query<Response<Vendor[]>, void>({
      query: () => ({
        url: "vendors",
        method: "GET",
      }),
      providesTags: ["Vendor"],
    }),
    getIntegratedVendors: builder.query<Response<Vendor[]>, void>({
      query: () => ({
        url: "vendors/integrated",
        method: "GET",
      }),
      providesTags: ["Vendor"],
    }),
    getVendorRemoteProducts: builder.query<
      Response<VendorRemoteProductsPayload>,
      string
    >({
      query: (vendorId) => ({
        url: `vendors/${vendorId}/remote-products`,
        method: "GET",
      }),
      providesTags: (_result, _error, vendorId) => [
        { type: "VendorRemoteProducts", id: vendorId },
      ],
    }),
    getVendor: builder.query<Response<Vendor>, string>({
      query: (id) => ({
        url: `vendors/${id}`,
        method: "GET",
      }),
      providesTags: ["Vendor"],
    }),
    createVendor: builder.mutation<Response<Vendor>, Partial<Vendor>>({
      query: (vendor) => ({
        url: "vendors",
        method: "POST",
        data: vendor,
      }),
      invalidatesTags: ["Vendor"],
    }),
    updateVendor: builder.mutation<
      Response<Vendor>,
      { id: string; data: Partial<Vendor> }
    >({
      query: ({ id, data }) => ({
        url: `vendors/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: ["Vendor"],
    }),
    deleteVendor: builder.mutation<Response<void>, string>({
      query: (id) => ({
        url: `vendors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Vendor"],
    }),
    toggleVendorStatus: builder.mutation<
      Response<Vendor>,
      { id: string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `vendors/${id}/status`,
        method: "PATCH",
        data: { is_active },
      }),
      invalidatesTags: ["Vendor"],
    }),
  }),
});

export const {
  useGetVendorsQuery,
  useGetIntegratedVendorsQuery,
  useGetVendorRemoteProductsQuery,
  useLazyGetVendorRemoteProductsQuery,
  useGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useToggleVendorStatusMutation,
} = vendorApi;

